import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { getCuposAlumno, getCuposCurso, ICupoSolicitud } from "../Api/reservation";
import { ISubjectQuotas, ISubjectsSliceState, IPanelSubjectData } from "../reduxSlices/subjectsSlice";
import { getCuposAlumnoFijados } from "../Api/reservation";
import { mergeSubjectsQuotas } from "../reduxSlices/subjectsSlice";
import { RootState } from "../app/store";
import { getCatalogoCondicionAprobCurso, getCurso, toggleMateriaFijadaAlumno } from "../Api/subject";
import { CoreSeccionOferta } from "../types/oferta";
import { IOfferedSubjectSection } from "../types";

/** Forma mínima compartida por la oferta del store y la que devuelve /materias. */
export type OfertaParaCupos = {
  tipoMateria?: string;
  sections?: {
    idCurso: number;
    anioLectivo: number;
    tipoMateria?: string;
    subjectData?: { reserveData?: { idC?: number; tim?: string } };
  }[];
}[];

/**
 * La oferta puede llegar por parámetro (quien acaba de pedir /materias) o leerse
 * del store. El tipo de materia viaja en reserveData.tim, que es el valor que
 * antes resolvía el backend contra core_tipo_cupo_alumno_x_curso.
 */
const normalizarTipoMateria = (...candidatos: (string | undefined)[]): ICupoSolicitud["tipoMateria"] | null => {
  for (const candidato of candidatos) {
    const valor = candidato?.trim().toUpperCase();
    if (valor === "OBLIGATORIO" || valor === "OPTATIVO") {
      return valor;
    }
  }
  return null;
};

const obtenerSolicitudesCupo = (state: RootState, oferta?: OfertaParaCupos | null) => {
  const materias = oferta ?? state.subjects.offeredSubjects ?? [];
  const fijados = state.subjects.pinnedSubjectsList ?? [];
  const unicos = new Map<number, { solicitud: ICupoSolicitud; fijado: boolean }>();

  materias.forEach((materia) => {
    (materia.sections ?? []).forEach((seccion) => {
      const reserveData = seccion.subjectData?.reserveData;
      const idCupo = reserveData?.idC ?? -1;
      const tipoMateria = normalizarTipoMateria(reserveData?.tim, seccion.tipoMateria, materia.tipoMateria);
      if (idCupo <= 0 || !tipoMateria) return;

      unicos.set(idCupo, {
        solicitud: { idCupo, tipoMateria },
        fijado: fijados.some((f) => f.idCurso === seccion.idCurso && f.anioLectivo === seccion.anioLectivo),
      });
    });
  });

  const valores = Array.from(unicos.values());
  if (valores.length === 0) {
    console.warn(`No se consultan cupos: la oferta no aportó cupos válidos (materias: ${materias.length})`);
  }
  return {
    fijados: valores.filter((c) => c.fijado).map((c) => c.solicitud),
    noFijados: valores.filter((c) => !c.fijado).map((c) => c.solicitud),
  };
};

/**
 * Deja en "cargando" los cupos que todavía no están en el store, así la oferta
 * muestra el loader desde que se pide la oferta hasta que responde el HTTP y,
 * para los que el backend encoló, hasta que llega la notificación por socket.
 * Los cupos que ya tienen un valor no se tocan para no volver a mostrar el
 * loader sobre un dato vigente.
 */
const sembrarCuposEnCarga = (solicitados: ICupoSolicitud[], actuales: ISubjectQuotas): ISubjectQuotas => {
  const enCarga: ISubjectQuotas = {};
  solicitados.forEach(({ idCupo }) => {
    if (actuales[idCupo]) return;
    enCarga[idCupo] = {
      quotaId: idCupo,
      qty: 0,
      availability: 0,
      cursoId: -1,
      anioLectivo: -1,
      readyToUse: false,
      lastUpdateTimestamp: 0,
    };
  });
  return enCarga;
};

/**
 * Corta el loader de los cupos que no volvieron en una respuesta que era final
 * (fijados) o que falló: por esos no va a llegar nada por socket.
 */
const cerrarCuposSinRespuesta = (
  solicitados: ICupoSolicitud[],
  recibidos: ISubjectQuotas,
  actuales: ISubjectQuotas,
): ISubjectQuotas => {
  const cerrados: ISubjectQuotas = {};
  solicitados.forEach(({ idCupo }) => {
    const actual = actuales[idCupo];
    if (recibidos[idCupo] || !actual || actual.readyToUse) return;
    cerrados[idCupo] = { ...actual, readyToUse: true };
  });
  return cerrados;
};

const mergearCupos = (
  dispatch: (action: ReturnType<typeof mergeSubjectsQuotas>) => void,
  cupos: ISubjectQuotas,
) => {
  if (Object.keys(cupos).length > 0) {
    dispatch(mergeSubjectsQuotas(cupos));
  }
};

export type GetCuposAlumnosArg = number | { idAlumno: number; oferta?: OfertaParaCupos | null };

const extraerArgCupos = (arg: GetCuposAlumnosArg) =>
  typeof arg === "number" ? { idAlumno: arg, oferta: undefined } : arg;

export const updateCupoCursoThunk = createAsyncThunk<ISubjectQuotas, any, { state: RootState }>(
  "front/updateCupoCurso",
  async (cupoId: number, thunkAPI) => {
    try {
      const response = await getCuposCurso(cupoId);
      return response.data as ISubjectQuotas;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateCupoCursoBuilder = (builder: ActionReducerMapBuilder<ISubjectsSliceState>) => {
  builder.addCase(updateCupoCursoThunk.fulfilled, (state, action) => {
    const newQuotas = { ...state.subjectsQuotas, ...action.payload };
    state.subjectsQuotas = newQuotas;
  });
  builder.addCase(updateCupoCursoThunk.rejected, (state, action) => {});
};


// ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
export const getCuposAlumnos = createAsyncThunk<ISubjectQuotas, GetCuposAlumnosArg, { state: RootState }>(
  "front/getCuposAlumno",
  async (arg, thunkAPI) => {
    try {
      const { idAlumno, oferta } = extraerArgCupos(arg);
      const idPersona = idAlumno.toString();
      const cupos = obtenerSolicitudesCupo(thunkAPI.getState(), oferta);
      let newQuotas: ISubjectQuotas = {};

      // Loader visible mientras se resuelven ambos endpoints
      mergearCupos(
        thunkAPI.dispatch,
        sembrarCuposEnCarga([...cupos.fijados, ...cupos.noFijados], thunkAPI.getState().subjects.subjectsQuotas),
      );

      // Fijadas: HTTP es la fuente final (no llegan por WS) → listos para usar al mergear
      if (cupos.fijados.length > 0) {
        const responseFijados = await getCuposAlumnoFijados(idPersona, cupos.fijados, true);
        const recibidosFijados = responseFijados.status === 200 && responseFijados.data ? responseFijados.data : null;
        if (!recibidosFijados) {
          console.warn("Error al obtener cupos fijados:", responseFijados.data);
        }
        const fijadosActualizados = {
          ...cerrarCuposSinRespuesta(
            cupos.fijados,
            recibidosFijados ?? {},
            thunkAPI.getState().subjects.subjectsQuotas,
          ),
          ...(recibidosFijados ?? {}),
        };
        newQuotas = { ...newQuotas, ...fijadosActualizados };
        console.log(`[get_cupos_alumno] ${Object.keys(recibidosFijados ?? {}).length} cupos fijados`);
        mergearCupos(thunkAPI.dispatch, fijadosActualizados);
      } else {
        console.log(`[get_cupos_alumno] No hay cupos fijados`);
      }

      if (cupos.noFijados.length === 0) return newQuotas;
      // El backend ya devuelve los cupos calculados → listos para usar
      const response = await getCuposAlumno(idPersona, cupos.noFijados, true);
      if (response.status !== 200 || !response.data) {
        // Sin respuesta no llega nada por socket: se corta el loader de este lote.
        mergearCupos(
          thunkAPI.dispatch,
          cerrarCuposSinRespuesta(cupos.noFijados, {}, thunkAPI.getState().subjects.subjectsQuotas),
        );
        return thunkAPI.rejectWithValue(response.data ?? "Error al obtener cupos");
      }

      const pendientes = cupos.noFijados.filter(({ idCupo }) => !response.data?.[idCupo]);
      newQuotas = { ...newQuotas, ...response.data };
      console.log(`[get_cupos_alumno] ${cupos.noFijados.length} cupos no fijados`);
      console.log(`[get_cupos_alumno] ${Object.keys(response.data).length} cupos recibidos de manera sincrónica`);
      console.log(`[get_cupos_alumno] ${pendientes.length} cupos pendientes vía socket`);
      mergearCupos(thunkAPI.dispatch, response.data);

      return newQuotas;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getCuposAlumnoListBuilder = (builder: ActionReducerMapBuilder<ISubjectsSliceState>) => {
  builder.addCase(getCuposAlumnos.pending, (state) => {
    state.getCuposAlumnoInFlight = true;
  });
  builder.addCase(getCuposAlumnos.fulfilled, (state, action) => {
    console.log("newQuotas:", Object.keys(action.payload).length);
    state.getCuposAlumnoFetched = true;
    state.getCuposAlumnoInFlight = false;
  });
  builder.addCase(getCuposAlumnos.rejected, (state) => {
    state.getCuposAlumnoInFlight = false;
  });
};

export const getCuposAlumnoFijadosThunk = createAsyncThunk<ISubjectQuotas, GetCuposAlumnosArg, { state: RootState }>(
  "front/getCuposAlumnoFijados",
  async (arg, thunkAPI) => {
    try {
      const { idAlumno, oferta } = extraerArgCupos(arg);
      const idPersona = idAlumno.toString();
      const cupos = obtenerSolicitudesCupo(thunkAPI.getState(), oferta).fijados;
      if (cupos.length === 0) return {};

      // Loader visible hasta que responda el endpoint
      mergearCupos(thunkAPI.dispatch, sembrarCuposEnCarga(cupos, thunkAPI.getState().subjects.subjectsQuotas));

      // Fijadas: HTTP es la fuente final (no llegan por WS) → listos para usar al mergear
      const responseFijados = await getCuposAlumnoFijados(idPersona, cupos, true);
      if (responseFijados.status !== 200 || !responseFijados.data) {
        mergearCupos(
          thunkAPI.dispatch,
          cerrarCuposSinRespuesta(cupos, {}, thunkAPI.getState().subjects.subjectsQuotas),
        );
        return thunkAPI.rejectWithValue(responseFijados.data ?? "Error al obtener cupos fijados");
      }

      mergearCupos(thunkAPI.dispatch, {
        ...cerrarCuposSinRespuesta(cupos, responseFijados.data, thunkAPI.getState().subjects.subjectsQuotas),
        ...responseFijados.data,
      });
      return responseFijados.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);
// ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
export const getCatalogoCondicionAprobCursoThunk = createAsyncThunk<
  IPanelSubjectData,
  { idCurso: number; anioLectivo: number; idAlumnoPrograma: number },
  { state: RootState }
>("front/getCatalogoCondicionAprobCurso", async ({ idCurso, anioLectivo, idAlumnoPrograma }, thunkAPI) => {
  try {
    const response = await getCatalogoCondicionAprobCurso(idCurso, anioLectivo);

    if (response.status !== 200) {
      return thunkAPI.rejectWithValue("Error al obtener datos del curso");
    }

    return {
      catalogo: response.data.catalogo,
      condicionAprob: response.data.condicionAprob,
      link: response.data.link,
      idCurso,
      anioLectivo,
      idAlumnoPrograma,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message);
  }
});

export const getCatalogoCondicionAprobCursoBuilder = (builder: ActionReducerMapBuilder<ISubjectsSliceState>) => {
  builder.addCase(getCatalogoCondicionAprobCursoThunk.fulfilled, (state, action) => {
    // Verificar si ya existe un elemento con el mismo idCurso
    const existingIndex = state.panelSubjectsDataList.findIndex(
      (item) => item.idCurso === action.payload.idCurso && item.anioLectivo === action.payload.anioLectivo
    );

    if (existingIndex !== -1) {
      // Actualizar el elemento existente
      state.panelSubjectsDataList[existingIndex] = action.payload;
    } else {
      // Agregar nuevo elemento
      state.panelSubjectsDataList.push(action.payload);
    }
  });
  builder.addCase(getCatalogoCondicionAprobCursoThunk.rejected, (state, action) => {
    console.error("Error al obtener catálogo y condición de aprobación:", action.payload);
  });
};
// ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
export const getCursoThunk = createAsyncThunk<
  CoreSeccionOferta,
  { idCurso: number; anioLectivo: number },
  { state: RootState }
>("front/getCurso", async ({ idCurso, anioLectivo }, thunkAPI) => {
  try {
    const response = await getCurso(idCurso, anioLectivo);
    if (response.status !== 200) {
      return thunkAPI.rejectWithValue("Error al obtener el curso");
    }
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message);
  }
});

export const getCursoBuilder = (builder: ActionReducerMapBuilder<ISubjectsSliceState>) => {
  builder.addCase(getCursoThunk.fulfilled, (state, action) => {
    const { id_materia, id_curso, data } = action.payload;
    const newSection = data as unknown as IOfferedSubjectSection;

    if (!state.offeredSubjects) {
      state.offeredSubjects = [{ id: id_materia, title: newSection.title ?? "", tipoMateria: newSection.tipoMateria ?? "", sections: [newSection] }];
      return;
    }

    const subject = state.offeredSubjects.find((s) => s.id === id_materia);

    if (!subject) {
      state.offeredSubjects.push({ id: id_materia, title: newSection.title ?? "", tipoMateria: newSection.tipoMateria ?? "", sections: [newSection] });
      return;
    }

    const section = subject.sections.find((s) => s.idCurso === id_curso);

    if (section) {
      Object.assign(section, newSection);
    } else {
      subject.sections.push(newSection);
    }
  });
  builder.addCase(getCursoThunk.rejected, (state, action) => {
    console.error("Error al obtener el curso:", action.payload);
  });
};
// ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------

export interface ToggleMateriaFijadaParams {
  idPersona: number;
  cursoId: number;
  anioLectivo: number;
}

export interface ToggleMateriaFijadaResponse {
  success: boolean;
  wasPinned: boolean; // Indica si estaba fijado antes del toggle
  isNowPinned: boolean; // Indica si está fijado después del toggle
}

export const toggleMateriaFijadaThunk = createAsyncThunk<
  ToggleMateriaFijadaResponse,
  ToggleMateriaFijadaParams,
  { state: RootState }
>("front/toggleMateriaFijada", async ({ idPersona, cursoId, anioLectivo }, thunkAPI) => {
  try {
    // Obtener el estado actual para determinar si estaba fijado
    const state = thunkAPI.getState();
    const wasPinned = state.subjects.pinnedSubjectsList.some(
      (item) => item.idCurso === cursoId && item.anioLectivo === anioLectivo
    );

    // Realizar el toggle
    const response = await toggleMateriaFijadaAlumno(idPersona, cursoId, anioLectivo);

    if (response.status !== 200) {
      return thunkAPI.rejectWithValue("Error al fijar/desfijar materia");
    }

    // El toggle invierte el estado, así que si estaba fijado, ahora no lo está y viceversa
    const isNowPinned = !wasPinned;

    return {
      success: true,
      wasPinned,
      isNowPinned,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "Error al fijar/desfijar materia");
  }
});

export const toggleMateriaFijadaBuilder = (builder: ActionReducerMapBuilder<ISubjectsSliceState>) => {
  builder
    .addCase(toggleMateriaFijadaThunk.pending, (state) => {
      // Opcional: agregar estado de loading si es necesario
      console.log("🔄 Toggle materia fijada en proceso...");
    })
    .addCase(toggleMateriaFijadaThunk.fulfilled, (state, action) => {
      const { cursoId, anioLectivo } = action.meta.arg;
      const { isNowPinned } = action.payload;

      if (isNowPinned) {
        // Agregar el curso a la lista si no existe
        const exists = state.pinnedSubjectsList.some(
          (item) => item.idCurso === cursoId && item.anioLectivo === anioLectivo
        );
        if (!exists) {
          state.pinnedSubjectsList.push({ idCurso: cursoId, anioLectivo });
          console.log(`✅ Curso ${cursoId} agregado a materias fijadas`);
        }
      } else {
        // Remover el curso de la lista
        const index = state.pinnedSubjectsList.findIndex(
          (item) => item.idCurso === cursoId && item.anioLectivo === anioLectivo
        );
        if (index !== -1) {
          state.pinnedSubjectsList.splice(index, 1);
          console.log(`✅ Curso ${cursoId} removido de materias fijadas`);
        }
      }
    })
    .addCase(toggleMateriaFijadaThunk.rejected, (state, action) => {
      console.error("❌ Error al fijar/desfijar materia:", action.payload);
    });
};
